import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sale } from '../../types/sales';
import { updateSale } from '../../features/sales/salesSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SaleFormContent from './SaleFormContent';

interface Props {
  sale: Sale;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditSaleForm: React.FC<Props> = ({ sale, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showVendorModal, setShowVendorModal] = useState(false);

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      crop_id: sale.crop_id,
      vendor_id: sale.vendor_id,
      quantity: String(sale.quantity),
      unit: sale.unit || 'kg',
      unit_price: String(sale.unit_price),
      price_unit: sale.price_unit || 'per_kg',
      purchase_price: String(sale.purchase_price || ''),
      sale_date: sale.sale_date.split('T')[0],
      notes: sale.notes || ''
    },
    {
      crop_id: (value) => (!value ? t('sales.validation.cropRequired') : ''),
      vendor_id: (value) => (!value ? t('sales.validation.vendorRequired') : ''),
      quantity: (value) => {
        if (!value) return t('sales.validation.quantityRequired');
        if (Number(value) <= 0) return t('sales.validation.quantityPositive');
        return '';
      },
      unit_price: (value) => {
        if (!value) return t('sales.validation.priceRequired');
        if (Number(value) <= 0) return t('sales.validation.pricePositive');
        return '';
      },
      sale_date: (value) => (!value ? t('sales.validation.dateRequired') : '')
    },
    async (values) => {
      await dispatch(updateSale({
        id: sale.id,
        ...values,
        quantity: Number(values.quantity),
        unit_price: Number(values.unit_price),
        purchase_price: values.purchase_price ? Number(values.purchase_price) : 0
      }));
      onSuccess();
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-6">
        <SaleFormContent
          values={values}
          errors={errors}
          onChange={handleChange}
          showVendorModal={showVendorModal}
          setShowVendorModal={setShowVendorModal}
        />

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default EditSaleForm;